import React, { useState, useEffect, useRef, useCallback } from 'react';

const BEAUTY_FACTS = [
  'AC and central heating both dehydrate your skin — a small humidifier can make more of a difference than you\'d expect.',
  '"Dehydrated" and "dry" are not the same thing. Dehydration is a lack of water; dryness is a lack of oil. You can be oily and dehydrated at the same time.',
  'Sunscreen and heavy makeup need an oil-based cleanser or cleansing balm to fully break down — a regular gel or foam won\'t do it on its own.',
  'Double cleansing means using an oil-based cleanser first to dissolve SPF and makeup, then a water-based cleanser to clean the skin. Skipping the first step means residue stays on your skin overnight.',
  'Double cleansing doesn\'t just remove makeup — it clears out pores more thoroughly than a single cleanse. If congestion is a concern, it\'s worth considering as a regular evening step.',
  'Your skin undertone — warm, cool, neutral, or olive — is genetic and never changes.',
  'Hard water leaves mineral residue on skin that can block pores and disrupt your skin barrier over time.',
  'Your scalp is skin. It needs cleansing and care just like the rest of your skin — but the products it needs may differ from what your hair strands may need.',
  'Retinol stimulates your skin to produce more collagen, which is why it plumps fine lines and firms skin over time. The trade-off is that it makes the outer skin layer temporarily more sensitive to UV — so SPF becomes non-negotiable when using it.',
  'The skin has layers. The outermost is the epidermis — what you see and touch. Beneath it is the dermis, where collagen and elastin live. Most anti-ageing products work on the epidermis; the most effective ones reach the dermis.',
  'The "pore-shrinking" claim on products is misleading. Pore size is genetic. What products can do is temporarily reduce their appearance — usually by clearing out the debris that makes them look larger.',
  'SPF in foundation rarely gives adequate protection. You\'d need to apply several times the normal amount to reach the stated SPF number.',
  'Mixing SPF into your moisturiser or foundation dilutes the UV filters and reduces their effectiveness. SPF works best applied as its own dedicated layer.',
  'Fragrance — including "natural" fragrance — is the most common cause of contact dermatitis in skincare.',
  'Niacinamide and vitamin C can be used together. The old advice about them cancelling each other out was based on outdated research.',
  'Your hands age faster than most other areas because they\'re exposed to UV year-round regardless of climate — and almost no one applies SPF to them.',
  'Your neck and décolletage age faster than your face because the skin is thinner and typically gets far less SPF — unless you live somewhere cold enough to stay covered up most of the year.',
  'The T-zone is oilier because it has more sebaceous glands per cm² than anywhere else on the face.',
  'Chemical exfoliants (AHAs, BHAs) are generally kinder to skin than scrubs — they dissolve the bonds between dead cells rather than physically tearing at them.',
  'Glycolic acid is an AHA — but unlike harsh scrubs, it\'s actually hydrating at the right concentration. It works on the surface to smooth texture and brighten, and is one of the most well-researched exfoliating acids.',
  'AHAs (like glycolic and lactic acid) work on the skin\'s surface — smoothing texture, brightening tone, fading dullness. BHAs (like salicylic acid) are oil-soluble, so they get inside pores — better for blackheads and congestion.',
  'Dead skin cells that don\'t shed properly make skin look dull, feel rough, and can cause uneven tone. AHAs gently dissolve the bonds holding them on, revealing the newer skin underneath.',
  'Hyaluronic acid is one of many humectants — ingredients that attract water to the skin. Glycerin does the same job, is often better tolerated, and costs a fraction of the price.',
  'L-ascorbic acid is the most potent form of vitamin C in skincare — but it\'s unstable, oxidises quickly, and can irritate sensitive skin. Stabilised derivatives like ascorbyl glucoside or tetrahexyldecyl ascorbate are gentler and last longer in the formula.',
  'L-ascorbic acid (the most potent form of vitamin C) is unstable and oxidises quickly when exposed to light and air. Store it in a cool, dark place and aim to use it within 3 months of opening. More stable vitamin C derivatives don\'t typically need the same level of care.',
  'If your L-ascorbic acid serum has turned orange or brown, or smells sour, it has oxidised and is no longer effective.',
  'Vitamin C helps neutralise free radicals, fades dark spots, and supports collagen production. Because it\'s acidic, it can irritate a compromised skin barrier — introducing it every other day reduces the chance of a reaction.',
  'Free radicals are unstable molecules — created by UV, pollution, and stress — that damage skin cells and accelerate ageing. Antioxidants like vitamin C, vitamin E, and niacinamide neutralise them before they cause damage. This is what "antioxidant protection" and "protects against pollution" mean on product labels.',
  'Skin cells take roughly 28 days to turn over when you\'re young, and slower with age. Most products need at least 4–6 weeks of consistent use before you can genuinely judge whether they\'re working.',
  'Switching products too early is one of the most common skincare mistakes. If you don\'t give something 4–6 weeks, you often never find out if it works.',
  'SPF 30 blocks ~97% of UVB rays; SPF 50 blocks ~98%. The difference sounds small — but most people underapply, and starting with a higher SPF means you still get meaningful protection at a half-applied dose.',
  'SPF 100 exists and blocks ~99% of UVB — but the difference between SPF 50 and SPF 100 is just 1% in theory. Consistent and generous application matters far more than the number on the label.',
  'Most people apply only 25–50% of the amount of sunscreen needed to actually reach the stated SPF.',
  'The recommended amount of sunscreen for the face is about half a teaspoon — roughly two finger-lengths of product. Most people use far less without realising it.',
  'Three types of moisturising ingredient do three different jobs. Humectants (glycerin, hyaluronic acid) attract water to the skin. Occlusives (petroleum jelly, shea butter) seal it in. Emollients (plant oils, fatty acids) smooth and soften. A well-made moisturiser usually combines all three.',
  '"Non-comedogenic" means a product is formulated not to clog pores — but the term isn\'t regulated. It originates from a study done on rabbit ears in the 1970s, and many dermatologists consider it unreliable. The real test is how your own skin responds.',
  'Transepidermal water loss (TEWL) is the process by which water evaporates through the skin into the air. A healthy skin barrier keeps TEWL low; when the barrier is compromised, water escapes faster — causing tightness, flakiness, and sensitivity.',
  'Petroleum jelly (Vaseline) is one of the most effective and well-researched moisturising ingredients — it seals moisture in without being absorbed into skin. Despite common belief, it doesn\'t clog pores for most people.',
  'You can have dry skin and still get clogged pores. Clogged pores are caused by a buildup of oil and dead skin cells — dry skin that isn\'t properly exfoliated can actually make congestion worse.',
  'The "drink more water for glowing skin" link is overstated. Skin hydration is more directly affected by your moisturiser and barrier health than by water intake.',
  'Ice-cold water doesn\'t close pores — pores don\'t have muscles and can\'t open or close. Cold water can temporarily reduce redness and puffiness by constricting blood vessels near the surface.',
  'Modern cleansers don\'t leave alkaline residue, so using toner to "balance your pH" after them isn\'t necessary with most current formulations.',
  'The order of your skincare routine generally matters — thinnest to thickest. But if two products have the same consistency, the order matters much less. The key rule: actives before moisturiser, moisturiser before SPF.',
  'Collagen molecules in "collagen creams" are too large to penetrate skin. They hydrate the surface, but don\'t replace the collagen underneath.',
  'Collagen is the protein that gives skin its structure and firmness. The body produces it naturally, but output starts slowing in your mid-20s — which is why skin gradually loses volume and develops fine lines.',
  'Glycerin pulls water towards the skin from the surrounding air. It\'s one of the most effective and affordable hydrating ingredients and works well across every price point.',
  'Whether micellar water needs rinsing depends on the formulation. Many leave a surfactant residue that can irritate skin with repeated use — check if yours is labelled rinse-free, or follow with a rinse-off cleanser to be safe.',
  'Sleep is when most skin repair happens — growth hormone peaks during deep sleep and drives collagen synthesis.',
  'Fatty alcohols (cetyl, stearyl) are moisturising. It\'s denatured alcohol (alcohol denat.) that strips the skin barrier and causes irritation with repeated use.',
  'Denatured alcohol evaporates quickly on skin, creating a cooling sensation — which is why it\'s used in toners and mattifying products. With daily use, it disrupts the skin barrier and can make skin more reactive over time.',
  'Climate affects how products perform. A rich cream that works well in dry winters can pill or cause breakouts in humid heat.',
  'Foundation oxidises on skin because it reacts with natural sebum — which is why your shade can shift in the hour after application.',
  'Blue light from screens has very weak evidence for causing skin damage compared to UV. Sunscreen matters far more than "blue light protection" products.',
  'Your skin barrier is made primarily of ceramides, fatty acids, and cholesterol — in roughly a 3:1:1 ratio.',
  'Eye creams are often the same formulation as your moisturiser in smaller packaging. The eye area is delicate — avoid anything heavily fragranced or too occlusive, and keep in mind it has limited options for corrective in-office procedures later on.',
  'Collagen production starts to slow in your mid-20s — but consistent SPF use from any age significantly slows the visible effects.',
  'Slugging (applying a thin layer of petroleum jelly as the last step at night) locks in moisture overnight and is one of the more effective barrier-repair strategies for very dry skin.',
  'Mineral sunscreens (zinc oxide, titanium dioxide) are often described as physical blockers that reflect UV — but they actually absorb the majority of UV radiation and only reflect a small percentage. The key distinction from chemical sunscreens is how they\'re formulated, not that one reflects and the other absorbs.',
  'Both mineral and chemical sunscreens are effective. Mineral formulas can be better tolerated by sensitive or reactive skin — but they tend to leave more of a white cast and can feel heavier.',
  'Most sunscreens on the market are a hybrid — combining both mineral and chemical UV filters. A product labelled "mineral" may still contain chemical filters. Check the active ingredients list if it matters to you.',
  'Skin produces less sebum in cold weather — which is why many people with oily skin in summer experience dryness in winter.',
  'Bakuchiol is marketed as a plant-based retinol alternative and is gentler on sensitive skin. However, the research behind it is limited compared to retinol, which has decades of clinical evidence.',
  'Retinol is one of the most evidence-backed ingredients in skincare — partly because it was originally developed as a pharmaceutical compound and has been studied at that level of rigour. Most skincare ingredients haven\'t been subjected to anywhere near the same amount of research.',
  'Most skincare ingredient studies are small, short-term, or funded by the brands selling the product. Ingredients with decades of rigorous research behind them (retinol, glycolic acid, SPF) are rare — worth remembering when a new "breakthrough" ingredient appears.',
  'Tapping products around the eye area causes less friction than rubbing — the skin there is among the thinnest on the body.',
  'Ceramides are the lipids that hold skin cells together — when the barrier is compromised, products with ceramides help rebuild it fastest.',
  'Using too many active ingredients at once doesn\'t give faster results — it often causes irritation and barrier damage that sets progress back.',
  'The skin around your lips has very few oil glands, which is why lips dry out faster than nearby skin.',
  'Patch testing on the inner arm takes 24–48 hours — but for your face, a small area on the jaw or behind the ear gives a more relevant result than the arm.',
  'SPF should be the last step in your skincare routine. Let it settle for a minute or two before applying primer or foundation — putting makeup on immediately can disrupt the UV filter layer before it sets.',
  'Niacinamide at 5–10% is one of the best-evidenced ingredients for reducing hyperpigmentation, minimising pore appearance, and strengthening the skin barrier.',
  'Applying moisturiser to slightly damp skin (within 60 seconds of cleansing) traps extra water and makes it more effective.',
  'Skin gets thinner with age because collagen and elastin production both slow down — this is why older skin bruises more easily and veins become more visible.',
  'PDRN (Polydeoxyribonucleotide) is a regenerative ingredient derived from salmon DNA. It\'s used in professional treatments to stimulate skin repair and hydration. Topical versions are emerging, but most of the clinical evidence is for injectable forms.',
  'NAD+ (nicotinamide adenine dinucleotide) is a coenzyme that declines with age and plays a role in cellular energy and repair. It\'s an active area of longevity research. NAD+ precursors are starting to appear in skincare products — but the evidence for topical use is still early.',
  'Peptides are short chains of amino acids — the building blocks of proteins like collagen and elastin. There are thousands of different peptides, each with a different function. "Contains peptides" on a label tells you very little without knowing which ones and at what concentration.',
];

const APP_TIPS = [
  'Upload a selfie and I\'ll analyse your undertone, skin type, and suggest foundation shades matched to you.',
  'Tell me a foundation you love and I\'ll help find the closest shade match for your skin tone.',
  'Ask me to build a full morning or evening routine — I\'ll tailor it to your skin type and concerns.',
  'You can save any product I recommend directly to your catalogue using the save button on any product card.',
  'Not sure where to start or what your skin type is? Upload a photo and I\'ll analyse it for you.',
  'You can ask me anything about skincare — I\'ll help guide you through it.',
  'Paste a product name or ingredient list and I\'ll tell you whether it suits your skin profile.',
  'Take a photo of a product and I\'ll analyse the ingredients and give you a breakdown.',
  'Ask me to compare two products you\'re considering — I\'ll look at ingredients and which suits your profile better.',
  'I remember your profile between conversations. The more you share about your skin, the more tailored my answers become.',
  'Ask me to explain any ingredient — I\'ll tell you what it does and whether it suits your skin.',
  'Add products you already own to your catalogue and I can review your routine for gaps or clashing ingredients.',
  'You can ask about routines for specific goals: "reduce redness", "prep skin for an event", or "integrate retinol into my routine".',
  'Tell me if a product broke you out — I\'ll help figure out what caused it and keep those ingredients out of future recommendations.',
  'Tell me what you already own and I\'ll fill the gaps rather than suggesting a complete rebuild.',
  'Ask me to review your full current routine — I\'ll spot anything that might be clashing or missing.',
  'Upload a photo of a product and I\'ll read the ingredient list and assess whether it suits your skin.',
  'Ask "what order should I apply my products?" and I\'ll sort your products into the right sequence.',
];

const JOURNEY_WAITING_COPY = {
  shade_skin_reference: [
    'Son:mi is checking visible depth, undertone and colour values against the exact range you chose.',
    'Lighting can shift how a shade appears, so the match also checks whether the photo is reliable enough to use.',
    'Foundation can oxidise after application. Treat a photo match as a shortlist, then check the closest shades in person when possible.',
  ],
  shade_estimate: [
    'This estimate uses the shade you already own as its reference; no skin photo is being analysed.',
    'Shade names are not standard across brands, so Son:mi compares depth and undertone rather than the name alone.',
  ],
  inspiration_reference: [
    'Son:mi is reading the makeup palette, contrast and finish in the reference—not treating the person as you.',
    'A skin photo can be added afterwards if you want the look adapted to your own colouring.',
  ],
  product_reference: [
    'Son:mi is identifying the brand and exact product from the label before using its product data.',
    'If several product lines have similar names, Son:mi may ask you to confirm the exact one.',
  ],
  focused_skin_area: [
    'This analysis stays limited to the area you selected and will not infer your whole-face skin type.',
    'Clear, even lighting helps separate visible texture from shadows and camera artefacts.',
  ],
  full_skin_scan: [
    'Son:mi is combining the visible signals across your face into one skin analysis.',
    'Even lighting and a sharp image make undertone, texture and visible concerns easier to assess.',
  ],
};

const FACT_HOLD_MS = 9000;
const CARD_CYCLE_MS = 7500;

function buildSequence(facts, tips) {
  const shuffledFacts = [...facts].sort(() => Math.random() - 0.5);
  const shuffledTips = [...tips].sort(() => Math.random() - 0.5);

  const seq = [];
  let fi = 0;
  let ti = 0;
  while (fi < shuffledFacts.length || ti < shuffledTips.length) {
    if (fi < shuffledFacts.length) seq.push({ type: 'fact', text: shuffledFacts[fi++] });
    if (fi < shuffledFacts.length) seq.push({ type: 'fact', text: shuffledFacts[fi++] });
    if (ti < shuffledTips.length) seq.push({ type: 'tip', text: shuffledTips[ti++] });
  }
  return seq;
}

// ─── Typing fact component (used inside loading bubble) ──────────────────────

export const TypingFact = ({ isLoading, topic = 'chat' }) => {
  const [displayText, setDisplayText] = useState('');
  const [isTypingDone, setIsTypingDone] = useState(false);
  const [opacity, setOpacity] = useState(1);
  const [currentItem, setCurrentItem] = useState(null);
  const sequenceRef = useRef([]);
  const indexRef = useRef(0);
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const startFact = useCallback(() => {
    const seq = sequenceRef.current;
    if (!seq.length) return;

    const item = seq[indexRef.current % seq.length];
    setCurrentItem(item);
    setDisplayText('');
    setIsTypingDone(false);
    setOpacity(1);

    const fullText = item.text;
    let charIndex = 0;

    const typeNextChar = () => {
      charIndex++;
      if (charIndex <= fullText.length) {
        setDisplayText(fullText.slice(0, charIndex));
        timerRef.current = setTimeout(typeNextChar, 16);
      } else {
        setIsTypingDone(true);
        timerRef.current = setTimeout(() => {
          setOpacity(0);
          timerRef.current = setTimeout(() => {
            indexRef.current = (indexRef.current + 1) % seq.length;
            startFact();
          }, 450);
        }, FACT_HOLD_MS);
      }
    };

    timerRef.current = setTimeout(typeNextChar, 16);
  }, []);

  useEffect(() => {
    if (!isLoading) {
      clearTimer();
      setDisplayText('');
      setCurrentItem(null);
      setIsTypingDone(false);
      setOpacity(1);
      return;
    }

    const journeyCopy = JOURNEY_WAITING_COPY[topic];
    sequenceRef.current = journeyCopy
      ? journeyCopy.map((text) => ({ type: 'status', text }))
      : buildSequence(BEAUTY_FACTS, APP_TIPS);
    indexRef.current = 0;
    startFact();

    return clearTimer;
  }, [isLoading, topic, startFact, clearTimer]);

  if (!isLoading) return null;

  return (
    <div
      style={{
        marginTop: '8px',
        paddingTop: '8px',
        borderTop: '1px solid rgba(42, 50, 75, 0.08)',
        fontSize: '13px',
        color: '#2A324B',
        lineHeight: '1.6',
        opacity,
        transition: 'opacity 0.4s ease',
        minHeight: '52px',
      }}
    >
      {currentItem && (
        <div style={{ fontSize: '13px', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#2A324B', opacity: 0.4, marginBottom: '4px' }}>
          {currentItem.type === 'status' ? 'What Son:mi is doing' : currentItem.type === 'fact' ? 'Skincare fact' : 'App tip'}
        </div>
      )}
      <span>
        {currentItem?.type === 'fact' && <strong>Did you know? </strong>}
        {displayText || '\u00A0'}
        {!isTypingDone && displayText.length > 0 && (
          <span className="tf-cursor">|</span>
        )}
      </span>
    </div>
  );
};

// ─── WaitingEntertainment card (below bubble, appears after 3s) ──────────────

const WaitingEntertainment = ({ isLoading }) => {
  const [visible, setVisible] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);
  const [animClass, setAnimClass] = useState('we-enter');
  const sequenceRef = useRef([]);

  useEffect(() => {
    sequenceRef.current = buildSequence(BEAUTY_FACTS, APP_TIPS);
  }, []);

  useEffect(() => {
    if (!isLoading) {
      setVisible(false);
      setCardIndex(0);
      return;
    }

    let cycleTimer;
    let transitionTimer;
    const showTimer = setTimeout(() => {
      setAnimClass('we-enter');
      setVisible(true);
      cycleTimer = setInterval(() => {
        setAnimClass('we-exit');
        transitionTimer = setTimeout(() => {
          setCardIndex(i => (i + 1) % sequenceRef.current.length);
          setAnimClass('we-enter');
        }, 420);
      }, CARD_CYCLE_MS);
    }, 3000);

    return () => {
      clearTimeout(showTimer);
      clearInterval(cycleTimer);
      clearTimeout(transitionTimer);
    };
  }, [isLoading]);

  if (!visible || !isLoading || sequenceRef.current.length === 0) return null;

  const card = sequenceRef.current[cardIndex];
  const isTip = card.type === 'tip';

  return (
    <div className={`mt-2 mr-auto max-w-[85%] sm:max-w-[90%] ${animClass}`}>
      <div
        style={{
          background: isTip ? '#fdf6f0' : '#f7f4f1',
          border: `1px solid ${isTip ? '#e8d5c4' : '#e0d8d0'}`,
          borderRadius: '14px',
          padding: '12px 16px',
          fontSize: '13px',
          color: '#2A324B',
          lineHeight: '1.55',
        }}
      >
        <div
          style={{
            fontSize: '13px',
            fontWeight: '600',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#341C1C',
            marginBottom: '6px',
            opacity: 0.7,
          }}
        >
          {isTip ? '✦ App tip' : '✦ Beauty fact'}
        </div>
        <div>{card.text}</div>
      </div>
    </div>
  );
};

export default WaitingEntertainment;
